// Which data state a screen is in, and when it is safe to save to the database.
// Pure functions: no screen code, no database.
//
// Rule: an account only ever sees its own numbers. There is no sample data in a signed-in view.
// While the numbers load, or when they fail to load, or when the account has no jobs yet, the screen says so.
// Nothing is saved to the database unless the numbers on screen are the account's own ("live").

// State of the account's data:
//   "loading": the first load has not finished
//   "error":   the first load failed (nothing to show; offer a retry)
//   "empty":   loaded fine, but there are no jobs with money yet
//   "live":    the account's own jobs are on screen
// If a later refresh fails while live data is already on screen, the live data stays ("live").
export function dataViewState({ loaded, loadError, jobCount } = {}) {
  if (Number(jobCount) > 0) return "live";
  if (loadError) return "error";
  if (!loaded) return "loading";
  return "empty";
}

// Ids that only ever belonged to the old built-in sample data (jobs J001-J010, expenses UNT001-UNT012).
// Real ids carry the account id or come from the database, so they never match.
export function isSampleId(id) {
  return typeof id === "string" && /^(J0\d\d|UNT0\d\d)$/.test(id);
}

// True when whatever is on screen must not be saved. Only "live" data may be written to the database.
export function isSampleMode(dataSource) {
  return dataSource !== "live";
}

// One check for every database write. ids: any job or expense ids the write would touch.
export function canSave(dataSource, ...ids) {
  if (isSampleMode(dataSource)) return false;
  return !ids.some(isSampleId);
}

// The public demo (app.canopybi.com/demo) shows made-up numbers with this data source. Like any non-"live" source,
// canSave refuses every database write for it.
export const DEMO_DATA_SOURCE = "demo";
export function isDemoSource(dataSource) {
  return dataSource === DEMO_DATA_SOURCE;
}

export const CANNOT_SAVE_NOTE = "Nothing was saved. Your own numbers are not on screen yet, so try again once they load.";

// The plain note to show when a write is refused: the demo has its own wording.
export function saveBlockedNote(dataSource, demoNote) {
  return isDemoSource(dataSource) ? demoNote : CANNOT_SAVE_NOTE;
}

// What an account with no jobs should do next. clientType is the account's setting ("quickbooks" or a
// spreadsheet type). qbConnected: QuickBooks is already connected.
// Returns { title, body, action } where action is "connect", "sync" or "upload".
export function emptyStateContent(clientType, qbConnected) {
  if (clientType === "quickbooks") {
    if (qbConnected) {
      return { title: "No jobs yet", body: "QuickBooks is connected, but no jobs with invoices or costs have come in yet. Sync QuickBooks to bring them in.", action: "sync" };
    }
    return { title: "No jobs yet", body: "Connect QuickBooks and Canopy will bring in your jobs, invoices and costs.", action: "connect" };
  }
  return { title: "No jobs yet", body: "Upload a spreadsheet of your jobs, revenue and expenses to see what each job made.", action: "upload" };
}

export const LOAD_ERROR_CONTENT = {
  title: "Your numbers could not load",
  body: "Something went wrong while getting your data. Nothing was changed. Check your connection and try again.",
};

export const LOADING_TEXT = "Loading your numbers…";
